(() => {
  function findVariant(variants, selectedOptions) {
    return variants.find((variant) => variant.options.every((value, index) => value === selectedOptions[index]));
  }

  function findAllocation(variant, planId) {
    return variant.allocations.find((allocation) => String(allocation.planId) === String(planId));
  }

  function formatMoney(amount, currency) {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount / 100);
  }

  function selectedOptions(root) {
    return [...root.querySelectorAll('[data-option-index]')].map(
      (fieldset) => fieldset.querySelector('input:checked')?.value
    );
  }

  function syncSellingPlans(root, variant, preferSubscription = false) {
    const subscription = root.querySelector('[data-purchase-type="subscription"]');
    const oneTime = root.querySelector('[data-purchase-type="one-time"]');
    const subscriptionCard = root.querySelector('[data-youngberry-subscription-card]');
    const planInput = root.querySelector('[data-youngberry-plan-input]');
    const allocation = variant?.allocations[0];

    subscriptionCard.hidden = !allocation;
    subscription.disabled = !allocation;
    if (!allocation) {
      oneTime.checked = true;
      planInput.value = '';
      return null;
    }

    subscription.dataset.planId = allocation.planId;
    if (preferSubscription) subscription.checked = true;
    planInput.value = subscription.checked ? allocation.planId : '';
    return allocation;
  }

  function setActiveMedia(root, mediaId) {
    if (!mediaId || !root.querySelector(`[data-youngberry-media="${mediaId}"]`)) return;
    root.querySelectorAll('[data-youngberry-media]').forEach((media) => {
      media.hidden = String(media.dataset.youngberryMedia) !== String(mediaId);
    });
    root.querySelectorAll('[data-youngberry-thumbnail]').forEach((thumbnail) => {
      thumbnail.setAttribute('aria-pressed', String(thumbnail.dataset.youngberryThumbnail) === String(mediaId));
    });
  }

  function setText(root, selector, value) {
    const element = root.querySelector(selector);
    if (element) element.textContent = value;
  }

  function updatePrices(root, variant, allocation) {
    const { currency, optionNames } = root.youngberryOfferData;
    const subscription = root.querySelector('[data-purchase-type="subscription"]');
    const chosenAllocation = allocation || variant.allocations[0];
    const supplyIndex = optionNames.indexOf('Supply');
    const supply = parseInt(variant.options[supplyIndex], 10) || 1;
    const oneTimeCompare = root.querySelector('[data-one-time-compare]');
    const subscriptionCompare = root.querySelector('[data-subscription-compare]');

    setText(root, '[data-one-time-price]', formatMoney(variant.price, currency));
    oneTimeCompare.hidden = !variant.compareAtPrice;
    oneTimeCompare.textContent = variant.compareAtPrice ? formatMoney(variant.compareAtPrice, currency) : '';

    if (chosenAllocation) {
      const subscriptionComparePrice = chosenAllocation.compareAtPrice ?? variant.price;
      setText(root, '[data-subscription-price]', formatMoney(chosenAllocation.price, currency));
      subscriptionCompare.hidden = subscriptionComparePrice == null;
      subscriptionCompare.textContent = subscriptionComparePrice == null
        ? ''
        : formatMoney(subscriptionComparePrice, currency);
      setText(
        root,
        '[data-subscription-savings]',
        `${root.dataset.savePrefix} ${formatMoney(variant.price - chosenAllocation.price, currency)}`
      );
      setText(root, '[data-subscription-cadence]', chosenAllocation.name);
      setText(
        root,
        '[data-subscription-daily]',
        `${formatMoney(chosenAllocation.perDeliveryPrice / supply, currency)} ${root.dataset.perDay}`
      );
    }

    const isSubscription = subscription.checked && chosenAllocation;
    setText(
      root,
      '[data-youngberry-main-price]',
      formatMoney(isSubscription ? chosenAllocation.price : variant.price, currency)
    );
    const selectedCard = root.querySelector('[name="purchase_type"]:checked')?.closest('label');
    setText(root, '[data-youngberry-main-label]', selectedCard?.querySelector('.youngberry-offer__purchase-copy strong')?.textContent || '');
  }

  function updateAvailability(root, variant) {
    const variantInput = root.querySelector('[data-youngberry-variant-input]');
    const submit = root.querySelector('[data-youngberry-submit]');
    const available = Boolean(variant?.available);

    variantInput.disabled = !variant;
    if (variant) variantInput.value = variant.id;
    submit.disabled = !available;
    submit.querySelector('span').textContent = variant
      ? available
        ? root.dataset.addLabel
        : root.dataset.soldOutLabel
      : root.dataset.unavailableLabel;
  }

  function updateUrl(root, variant) {
    const url = new URL(window.location.href);
    const plan = root.querySelector('[data-youngberry-plan-input]').value;
    url.searchParams.set('variant', variant.id);
    if (plan) url.searchParams.set('selling_plan', plan);
    else url.searchParams.delete('selling_plan');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }

  function refresh(root, preferSubscription = false, updateMedia = true) {
    const variant = findVariant(root.youngberryOfferData.variants, selectedOptions(root));
    updateAvailability(root, variant);
    if (!variant) return;
    const allocation = syncSellingPlans(root, variant, preferSubscription);
    updatePrices(root, variant, allocation);
    if (updateMedia) setActiveMedia(root, variant.featuredMediaId);
    updateUrl(root, variant);
  }

  function bindFlavorCards() {
    if (document.documentElement.dataset.youngberryFlavorCardsBound) return;
    document.documentElement.dataset.youngberryFlavorCardsBound = 'true';
    document.addEventListener('click', (event) => {
      const action = event.target.closest('[data-youngberry-flavor]');
      if (!action) return;
      const root = document.querySelector('[data-youngberry-offer]');
      const flavor = [...root?.querySelectorAll('[data-option-name="Flavor"] input') || []].find(
        (input) => input.value === action.dataset.youngberryFlavor
      );
      if (!flavor) return;
      event.preventDefault();
      flavor.checked = true;
      refresh(root);
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      root.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    });
  }

  function init(root) {
    if (!root || root.dataset.youngberryInitialized) return;
    const data = root.querySelector('[data-youngberry-variants]');
    if (!data) return;
    root.dataset.youngberryInitialized = 'true';
    root.youngberryOfferData = JSON.parse(data.textContent);

    root.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-index] input')) refresh(root);
      if (event.target.matches('[name="purchase_type"]')) refresh(root, false, false);
    });
    root.addEventListener('click', (event) => {
      const thumbnail = event.target.closest('[data-youngberry-thumbnail]');
      if (thumbnail) setActiveMedia(root, thumbnail.dataset.youngberryThumbnail);
    });

    refresh(root, true);
    bindFlavorCards();
  }

  function initAll(scope = document) {
    if (scope.matches?.('[data-youngberry-offer]')) init(scope);
    scope.querySelectorAll?.('[data-youngberry-offer]').forEach(init);
  }

  window.YoungberryOffer = { findVariant, findAllocation, syncSellingPlans, setActiveMedia, init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
